import { useMemo } from "react";
import styled from "styled-components";

import useIsBelowWidth from "@core/hooks/useIsBelowWidth";

interface Props {
  children: string;
}

interface Parsed {
  raidName: string;
  hard: string;
  normal: string;
  single: string;
  nightmare: string;
  none: string;
}

const RaidNameParser = ({ children }: Props) => {
  const isBelowWidth500 = useIsBelowWidth(500);
  const hardText = isBelowWidth500 ? "하" : "하드";
  const normalText = isBelowWidth500 ? "노" : "노말";
  const singleText = isBelowWidth500 ? "싱" : "싱글";
  const nightmareText = isBelowWidth500 ? "나" : "나이트메어";

  const parsed = useMemo(() => {
    const [raidName, rest] = children.split("<br />");

    const result: Parsed = {
      raidName,
      normal:
        children
          .match(/(노말)(?<captured>(\s*\d)+)/)
          ?.groups?.captured.trim() || "",
      hard:
        children
          .match(/(하드)(?<captured>(\s*\d)+)/)
          ?.groups?.captured.trim() || "",
      single:
        children
          .match(/(싱글)(?<captured>(\s*\d)+)/)
          ?.groups?.captured.trim() || "",
      nightmare:
        children
          .match(/(나이트메어)(?<captured>(\s*\d)+)/)
          ?.groups?.captured.trim() || "",
      none: rest,
    };

    return result;
  }, [children]);

  return (
    <Wrapper>
      <Row>{parsed.raidName}</Row>
      {(() => {
        const difficulties = [];

        if (parsed.normal) {
          difficulties.push({ text: normalText, value: parsed.normal, type: "normal" });
        }
        if (parsed.hard) {
          difficulties.push({ text: hardText, value: parsed.hard, type: "hard" });
        }
        if (parsed.single) {
          difficulties.push({ text: singleText, value: parsed.single, type: "single" });
        }
        if (parsed.nightmare) {
          difficulties.push({ text: nightmareText, value: parsed.nightmare, type: "nightmare" });
        }

        // 게이트 숫자 순서대로 정렬
        difficulties.sort((a, b) => Number(a.value) - Number(b.value));

        if (difficulties.length > 0) {
          return (
            <RowWithDifficulty>
              {difficulties.map((diff, index) => (
                <Difficulty key={index}>
                  <Label $difficulty={diff.type}>{diff.text}</Label>
                  {diff.value}
                </Difficulty>
              ))}
            </RowWithDifficulty>
          );
        }

        return <Row>{parsed.none}</Row>;
      })()}
    </Wrapper>
  );
};

export default RaidNameParser;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-width: 0; /* [overflow-fix] 부모 flex에서 축소 허용 */
  font-size: 14px;
  gap: 2px;
`;

// [overflow-fix] block 컨테이너여야 text-overflow: ellipsis가 실제로 동작.
// 이전엔 display:flex라 ellipsis가 자식 텍스트에 적용되지 않아 레이드 이름이 잘리지 않는 문제가 있었음.
const Row = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// 난이도 뱃지를 flex row로 배치해야 하므로 Row(block)와 분리 정의
const RowWithDifficulty = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  min-width: 0;
  max-width: 100%;

  ${({ theme }) => theme.medias.max400} {
    font-size: 12px;
  }
`;

const Difficulty = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  & + & {
    margin-left: 3px;
  }
`;

export const Label = styled.span<{ $difficulty?: string }>`
  margin-right: 2px;
  border-radius: 4px;
  padding: 1px 4px;
  font-size: 11px;
  font-weight: 700;
  text-decoration: normal !important;

  color: ${({ $difficulty, theme }) => {
    switch ($difficulty) {
      case "hard":
        return theme.app.text.red;
      case "nightmare":
        return theme.app.text.purple;
      case "single":
        return theme.app.text.dark2;
      case "normal":
      default:
        return theme.app.text.blue;
    }
  }};

  background: ${({ $difficulty }) => {
    switch ($difficulty) {
      case "hard":
        return "rgba(255, 80, 80, 0.12)";
      case "nightmare":
        return "rgba(160, 80, 255, 0.12)";
      case "single":
        return "rgba(150, 150, 150, 0.15)";
      case "normal":
      default:
        return "rgba(80, 140, 255, 0.12)";
    }
  }};
`;
